/**
 * Läsbar beskrivning av en regeluppsättning.
 *
 * OBS: detta är presentation, inte validering. Valideringen sker enbart i
 * databasen (validate_picks). Frestelsen att "medan jag ändå läser reglerna
 * här kan jag väl kontrollera dem också" är precis hur två regelmotorer
 * uppstår och börjar säga olika saker.
 */

export interface Constraint {
  type: string
  params?: Record<string, number>
}

export interface Ruleset {
  team_size?: number
  constraints?: Constraint[]
  cut_penalty?: { mode: string; steps: number[] }
  wd_penalty?: { mode: string; strokes: number }
  tiebreakers?: string[]
}

const REGION_LABEL: Record<string, string> = {
  USA: 'USA',
  EUROPE: 'Europa',
  ROW: 'övriga världen',
}

const TIEBREAK_LABEL: Record<string, string> = {
  total_to_par: 'lägst totalscore',
  fewest_usa: 'minst antal USA-spelare',
  best_nordic: 'bästa nordiska placering',
  highest_rank_sum: 'högst rankingsumma',
}

function regionList(params: Record<string, number>, verb: string): string {
  const parts = Object.entries(params).map(
    ([region, n]) => `${n} från ${REGION_LABEL[region] ?? region}`,
  )
  return `${verb} ${parts.join(', ')}.`
}

export function describeRuleset(ruleset: Ruleset | null | undefined): string[] {
  if (!ruleset) return ['Inga regler upplagda.']

  const out: string[] = [`Laget består av ${ruleset.team_size ?? 6} spelare.`]

  for (const c of ruleset.constraints ?? []) {
    const p = c.params ?? {}
    switch (c.type) {
      case 'region_exact':
        out.push(regionList(p, 'Exakt'))
        break
      case 'region_min':
        out.push(regionList(p, 'Minst'))
        break
      case 'max_top_n':
        out.push(`Högst ${p.max} spelare rankade topp ${p.n}.`)
        break
      case 'min_rank_sum':
        out.push(`Rankingsumman måste vara minst ${p.min} — tvingar fram value-picks.`)
        break
      case 'max_rank_sum':
        out.push(`Rankingsumman får vara högst ${p.max}.`)
        break
      case 'min_distinct_countries':
        out.push(`Minst ${p.min} olika länder.`)
        break
      default:
        // Ett okänt villkor genomdrivs ändå av databasen. Att tiga om det i
        // gränssnittet vore värre än att visa det obearbetat.
        out.push(`Villkor: ${c.type}.`)
    }
  }

  if (ruleset.cut_penalty?.mode === 'graded') {
    const [one, two, three] = ruleset.cut_penalty.steps
    out.push(`Missad cut kostar +${one} slag, två missade +${two}, tre eller fler +${three}.`)
  }

  if (ruleset.wd_penalty?.mode === 'cutline_plus') {
    out.push(
      `Spelare som bryter eller inte slår ut får cut-linjen +${ruleset.wd_penalty.strokes} slag.`,
    )
  }

  if (ruleset.tiebreakers?.length) {
    const labels = ruleset.tiebreakers.map((t) => TIEBREAK_LABEL[t] ?? t)
    out.push(`Vid lika: ${labels.join(', sedan ')}.`)
  }

  return out
}
